import { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import logoDLA from '../images/logoDLA.png';

const NAVBAR_OFFSET = 72;

const linkClass = ({ isActive }) =>
    `navbar-link fustat text-light text-content text-deco-none text-nowrap${isActive ? ' navbar-link--active' : ''}`;

function getNavbarTheme(isCompact) {
    const navbar = document.querySelector('.navbar-sticky');
    let y = (navbar?.getBoundingClientRect().bottom ?? NAVBAR_OFFSET) + 1;
    const x = isCompact ? window.innerWidth * 0.75 : window.innerWidth / 2;

    let element = document.elementFromPoint(x, y);
    let attempts = 0;

    while (
        element &&
        (element.closest('.navbar-sticky') || element.closest('.navbar-links--mobile')) &&
        attempts < 5
    ) {
        y += 20;
        element = document.elementFromPoint(x, y);
        attempts += 1;
    }

    return element?.closest('[data-navbar-theme]')?.dataset.navbarTheme || 'light';
}

function LanguageToggle() {
    const { i18n, t } = useTranslation();
    const isFrench = i18n.language.startsWith('fr');
    const targetLang = isFrench ? 'en' : 'fr';
    const label = isFrench ? 'EN' : 'FR';

    return (
        <button
            type="button"
            className="navbar-lang-toggle fustat text-light text-content"
            onClick={() => i18n.changeLanguage(targetLang)}
            aria-label={isFrench ? t('nav.switchToEn') : t('nav.switchToFr')}
        >
            {label}
        </button>
    );
}

function Navbar({ compact, navOpacity = 1, navBlur = 0 }) {
    const { t } = useTranslation();
    const isHomeNav = compact !== undefined;
    const isCompact = isHomeNav && compact;
    const opacity = isHomeNav ? navOpacity : 1;
    const blur = isHomeNav ? navBlur : 0;
    const location = useLocation();
    const [theme, setTheme] = useState('light');
    const [menuOpen, setMenuOpen] = useState(false);

    const navLinks = [
        { to: '/', label: t('nav.home'), end: true },
        { to: '/propos', label: t('nav.about') },
        { to: '/services', label: t('nav.services') },
        { to: '/realisations', label: t('nav.projects') },
        { to: '/carriere', label: t('nav.careers') },
        { to: '/nousjoindre', label: t('nav.contact') },
    ];

    useEffect(() => {
        const updateTheme = () => {
            setTheme(getNavbarTheme(isHomeNav ? isCompact : false));
        };

        updateTheme();

        window.addEventListener('scroll', updateTheme, { passive: true });
        window.addEventListener('resize', updateTheme);

        return () => {
            window.removeEventListener('scroll', updateTheme);
            window.removeEventListener('resize', updateTheme);
        };
    }, [location.pathname, isHomeNav, isCompact]);

    useEffect(() => {
        setMenuOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        if (!menuOpen) return undefined;

        document.body.style.overflow = 'hidden';

        const handleEscape = (event) => {
            if (event.key === 'Escape') setMenuOpen(false);
        };

        window.addEventListener('keydown', handleEscape);

        return () => {
            document.body.style.overflow = '';
            window.removeEventListener('keydown', handleEscape);
        };
    }, [menuOpen]);

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth > 1023) setMenuOpen(false);
        };

        window.addEventListener('resize', handleResize);

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const toggleMenu = () => setMenuOpen((open) => !open);

    return (
        <nav
            className={[
                'navbar-sticky',
                `navbar-theme-${theme}`,
                isHomeNav ? 'navbar-home' : 'navbar-sticky--full',
                isHomeNav && (isCompact ? 'navbar-home--compact' : 'navbar-home--full'),
                menuOpen ? 'navbar-menu-open' : '',
            ].filter(Boolean).join(' ')}
            style={{
                opacity,
                pointerEvents: opacity < 0.05 ? 'none' : 'auto',
            }}
        >
            <div
                className="navbar-inner flex items-center px-16 py-16"
                style={{ filter: blur > 0 ? `blur(${blur}px)` : 'none' }}
            >
                {!isCompact && (
                    <NavLink to="/" end className="navbar-logo-link shrink-0" onClick={() => setMenuOpen(false)}>
                        <img src={logoDLA} alt="Groupe DLA" height={31.5} className="navbar-logo" />
                    </NavLink>
                )}

                <div
                    className={
                        isCompact
                            ? 'navbar-links navbar-links--desktop flex flex-1 gap-32 items-center justify-center flex-nowrap'
                            : 'navbar-links navbar-links--desktop absolute inset-x-0 flex gap-32 items-center justify-center flex-nowrap'
                    }
                >
                    {navLinks.map(({ to, label, end }) => (
                        <NavLink key={to} to={to} end={end} className={linkClass}>
                            <span className="navbar-link-text">
                                <span className="navbar-link-text-inner">
                                    <span>{label}</span>
                                    <span aria-hidden="true">{label}</span>
                                </span>
                            </span>
                        </NavLink>
                    ))}
                </div>

                <div className="navbar-actions">
                    <LanguageToggle />
                    <button
                        type="button"
                        className={`navbar-toggle${menuOpen ? ' navbar-toggle--open' : ''}`}
                        aria-expanded={menuOpen}
                        aria-controls="navbar-menu"
                        aria-label={menuOpen ? t('nav.closeMenu') : t('nav.openMenu')}
                        onClick={toggleMenu}
                    >
                        <span className="navbar-toggle__bar" aria-hidden="true" />
                        <span className="navbar-toggle__bar" aria-hidden="true" />
                        <span className="navbar-toggle__bar" aria-hidden="true" />
                    </button>
                </div>
            </div>

            <div
                id="navbar-menu"
                className={`navbar-links navbar-links--mobile${menuOpen ? ' navbar-links--open' : ''}`}
                aria-hidden={!menuOpen}
            >
                {navLinks.map(({ to, label, end }) => (
                    <NavLink key={to} to={to} end={end} className={linkClass} onClick={() => setMenuOpen(false)}>
                        <span className="navbar-link-text">
                            <span className="navbar-link-text-inner">
                                <span>{label}</span>
                                <span aria-hidden="true">{label}</span>
                            </span>
                        </span>
                    </NavLink>
                ))}
            </div>
        </nav>
    );
}

export default Navbar;
